import React, { useEffect, useState, useRef } from 'react';
import { XIcon, MessageCircleIcon } from 'lucide-react';
type WhatsAppExitModalProps = {
  /** WhatsApp link (with prefilled text) to send the training. */
  whatsappLink: string;
  /** Optional callback fired when the modal is shown (e.g. pixel event). */
  onShow?: () => void;
  /** Optional callback fired when the WhatsApp CTA is clicked. */
  onAccept?: () => void;
  /**
   * Only arm the exit listeners while true. This popup is an EARLY-phase
   * recovery (before the pitch/offer opens), so the page disables it once the
   * offer reveals and hands exit intent over to the purchase-intent popup.
   */
  enabled?: boolean;
};
const SESSION_KEY = 'vsl_whatsapp_exit_shown';
/**
 * Early exit-intent popup. When a viewer tries to leave (cursor exits the top
 * of the viewport on desktop, or the back button on mobile), offer to send
 * them the training over WhatsApp so they can watch it later. Shows at most
 * once per session.
 */
export function WhatsAppExitModal({
  whatsappLink,
  onShow,
  onAccept,
  enabled = true
}: WhatsAppExitModalProps) {
  const [open, setOpen] = useState(false);
  const firedRef = useRef(false);
  const trigger = () => {
    if (!enabled) return;
    if (firedRef.current) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {}
    firedRef.current = true;
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {}
    setOpen(true);
    onShow?.();
  };
  useEffect(() => {
    // Only arm the early WhatsApp exit popup before the pitch opens.
    if (!enabled) return;
    // DESKTOP: cursor leaves through the top edge of the document.
    const onMouseOut = (e: MouseEvent) => {
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };
    document.addEventListener('mouseout', onMouseOut);
    // MOBILE / back button: push a state so the first back press opens the
    // modal instead of leaving the page.
    try {
      window.history.pushState({
        waExitGuard: true
      }, '');
    } catch {}
    const onPopState = () => trigger();
    window.addEventListener('popstate', onPopState);
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('popstate', onPopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!open) return null;
  return <div role="dialog" aria-modal="true" aria-label="¿Estás ocupado ahorita?" className="fixed inset-0 z-[125] flex items-center justify-center p-4 sm:p-6" data-id="element-216" data-mp="2ih34arvlal2n">
      <style data-id="element-217" data-mp="2nd1qq9xcxt0f">{`@keyframes waExitPop{0%{transform:scale(.9)translateY(8px);opacity:0}60%{transform:scale(1.02)}100%{transform:scale(1)translateY(0);opacity:1}}.wa-exit-pop{animation:waExitPop .4s cubic-bezier(.34,1.56,.64,1) both}`}</style>

      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} data-id="element-218" data-mp="25wwdwpdt7ydo" />

      <div className="wa-exit-pop relative w-full max-w-[460px] overflow-hidden rounded-2xl bg-[#0f172a] border border-[#1e293b] shadow-[0_30px_80px_rgba(0,0,0,0.6)] text-center" data-id="element-219" data-mp="20xkxjhhcuuwm">
        <button type="button" onClick={() => setOpen(false)} aria-label="Cerrar" className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-white/50 hover:bg-white/10 hover:text-white transition-colors" data-id="element-220" data-mp="1s5rb5ef5apz4">
          <XIcon className="h-5 w-5" data-id="element-221" data-mp="1i9y3a8cdfkno" />
        </button>

        <div className="px-6 sm:px-9 py-9" data-id="element-222" data-mp="1d9jwsuqf4irr">
          <span className="inline-block rounded-full bg-[#25D366]/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[1.5px] text-[#25D366] mb-4" data-id="element-223" data-mp="1d88oqipxwouu">
            ¡Espera!
          </span>

          <h2 className="font-['Anton'] uppercase tracking-[0.5px] text-[clamp(22px,5.5vw,32px)] leading-[1.05] text-white mb-3" data-id="element-224" data-mp="1xymdipj66p60">
            ¿Estás ocupado ahorita?
          </h2>

          <p className="text-[14px] sm:text-[15px] leading-relaxed text-white/70 mb-6" data-id="element-225" data-mp="1p4pxlsglm8z1">
            No pierdas el entrenamiento. Escríbeme por WhatsApp y te lo mando
            completo para que lo veas a tu conveniencia, cuando tengas tiempo.
          </p>

          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" onClick={() => {
          onAccept?.();
          setOpen(false);
        }} className="inline-flex items-center justify-center gap-2.5 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-extrabold text-[17px] tracking-tight p-4 rounded-lg transition-colors shadow-[0_6px_20px_rgba(37,211,102,0.35)]" data-id="element-226" data-mp="1k6o9gt4bfjgp">
            <MessageCircleIcon className="w-5 h-5 shrink-0" data-id="element-227" data-mp="30j3egdcxpo5k" />
            Mándame el entrenamiento
          </a>

          <button type="button" onClick={() => setOpen(false)} className="mt-4 text-[12.5px] text-white/40 hover:text-white/70 underline underline-offset-2 transition-colors" data-id="element-228" data-mp="sz60i4rxsci4">
            No, gracias — seguiré viendo
          </button>
        </div>
      </div>
    </div>;
}